import React, { useId } from 'react';
import Logo from './Logo';

/**
 * Dark side panel shown on the login/register pages (desktop only).
 * Carries the brand mark, a page-specific headline, an animated trend-line
 * motif (same draw-in style as the header Logo), and a short list of
 * feature highlights in a soft glass card.
 */
export default function AuthHero({ headline, points = [] }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const lineGradId = `sxhero-line-${uid}`;
  const keyframeName = `sxhero-draw-${uid}`;

  return (
    <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-[#161233] px-9 py-10 text-white lg:flex">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      <div
        className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)' }}
      />
      <div
        className="pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(circle, #22d3ee 0%, transparent 70%)' }}
      />

      <div className="relative">
        <div className="inline-flex items-center gap-2.5">
          <Logo size={30} />
          <span className="font-display text-lg font-semibold tracking-tight">StockXignal</span>
        </div>
        <h2 className="font-display mt-14 max-w-xs text-[1.75rem] font-semibold leading-[1.2] tracking-tight bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
          {headline}
        </h2>
      </div>

      <div className="relative">
        <svg viewBox="0 0 320 100" className="w-full max-w-[280px]" fill="none" aria-hidden="true">
          <defs>
            <linearGradient id={lineGradId} x1="0" y1="0" x2="320" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#22d3ee" />
              <stop offset="55%" stopColor="#818cf8" />
              <stop offset="100%" stopColor="#a78bfa" />
            </linearGradient>
          </defs>
          <polyline
            points="0,78 32,68 60,74 92,46 128,54 158,26 188,36 220,14 250,22 284,8 320,16"
            stroke={`url(#${lineGradId})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="100"
            style={{ strokeDasharray: 100, strokeDashoffset: 100, animation: `${keyframeName} 2.6s ease-out forwards` }}
          />
          <circle cx="92" cy="46" r="4" fill="#0c0f18" stroke="#22d3ee" strokeWidth="2" />
          <circle cx="220" cy="14" r="4" fill="#0c0f18" stroke="#a78bfa" strokeWidth="2" />
          <style>{`@keyframes ${keyframeName} { 0% { stroke-dashoffset: 100; } 100% { stroke-dashoffset: 0; } }`}</style>
        </svg>

        {points.length > 0 && (
          <ul className="mt-6 space-y-2.5 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200 backdrop-blur-sm">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-2.5">
                <svg className="mt-0.5 h-4 w-4 flex-none text-brand-300" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M16.704 5.29a1 1 0 010 1.42l-7.004 7a1 1 0 01-1.414 0l-3.004-3a1 1 0 111.414-1.42l2.297 2.294 6.297-6.294a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
